import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { createRole, updateRole } from '../services/api/roleApi';
import { getAllPermissions } from '../services/api/permissionsApi';
import { validateInput, validateObject } from '../utils/sanitizer';

const RoleForm = ({ role, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissionIds: []
  });
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const response = await getAllPermissions();
        setAvailablePermissions(response.data);
      } catch (err) {
        setError('Failed to load permissions. Please try again.');
      }
    };
    
    fetchPermissions();
    
    if (role) {
      setFormData({
        name: role.name || '',
        description: role.description || '',
        permissionIds: []
      });
    }
  }, [role]);

  

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Validate input but don't modify it
    if (!validateInput(value)) {
      setError('Input contains potentially unsafe content.');
      return;
    }
    
    setFormData({
      ...formData,
      [name]: value
    });
  };


  const handlePermissionChange = (selectedOptions) => {
    const permissionIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setFormData(prev => ({ ...prev, permissionIds }));
  };

  const getGroupedPermissions = () => {
    const grouped = availablePermissions.reduce((acc, permission) => {
      const resource = permission.resource;
      if (!acc[resource]) {
        acc[resource] = [];
      }
      acc[resource].push({
        value: permission.id,
        label: permission.action,
        fullLabel: `${permission.action}:${permission.resource}`,
        description: permission.description
      });
      return acc;
    }, {});

    return Object.keys(grouped)
      .sort()
      .map(resource => ({
        label: resource,
        options: grouped[resource].sort((a, b) => a.label.localeCompare(b.label))
      }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    // Validate input before submission
    const validation = validateObject(formData);
    if (!validation.isValid) {
      setError('Input contains potentially unsafe content.');
      setLoading(false);
      return;
    }

    try {
      if (role) {
        await updateRole(role.id, formData);
        setSuccess('Role updated successfully!');
      } else {
        await createRole(formData);
        setSuccess('Role created successfully!');
      }
      setTimeout(() => {
        onSave();
      }, 1500);
    } catch (err) {
      let errorMessage = 'Role save failed. Check your input or try again later.';
      
      if (err.response?.status === 400) {
        errorMessage = err.response?.data?.message || 'Invalid role information provided. Please check your input.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Error occurred while creating Role. Please contact support if the problem persists.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="row justify-content-center">
        <div className="col-md-6 col-lg-5">
          <div className="card">
            <div className="card-header">
              <h3 className="mb-0">{role ? 'Edit Role' : 'Create Role'}</h3>
            </div>
            <div className="card-body">
              <div className="user-form">
                {error && <div className="alert alert-error">{error}</div>}
                {success && <div className="alert alert-success">{success}</div>}
                
                <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">Name:</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description:</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
            className="form-control"
            placeholder="Enter role description..."
          />
        </div>

        {!role && (
          <div className="form-group">
            <label htmlFor="permissions">Permissions:</label>
            <Select
              isMulti
              options={getGroupedPermissions()}
              value={availablePermissions
                .filter(permission => formData.permissionIds.includes(permission.id))
                .map(permission => ({
                  value: permission.id,
                  label: permission.description || `${permission.action}:${permission.resource}`
                }))}
              onChange={handlePermissionChange}
              placeholder="Select permissions..."
              formatOptionLabel={(option) => (
                <div>
                  <div>{option.label}</div>
                  {option.description && <div style={{ fontSize: '0.85em', color: '#666' }}>{option.description}</div>}
                </div>
              )}
              getOptionLabel={(option) => option.description || option.fullLabel || option.label}
            />
          </div>
        )}

                  <div className="form-actions">
                    <button type="submit" disabled={loading} className="btn btn-primary">
                      {loading ? 'Saving...' : (role ? 'Update' : 'Create')}
                    </button>
                    <button type="button" onClick={onCancel} className="btn btn-secondary">
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoleForm;